import {
  Component,
  TemplateRef,
  ViewChild,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import {
  OrganizationDrawerParams,
  OrganizationModel,
  UserResultModel,
} from '../organization.interface';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DrawerService } from '../../../core/services/drawer.service';
import { OrganizationService } from '../organization.service';
import { OrgMode, OrgTitles } from '../organization.enums';
import { OrgManagementMode } from '../../manage-organization/manage-organization.enums';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { OrgContactFormComponent } from '../org-contact-form/org-contact-form.component';
import { DRAWER_IDS } from '../../../core/constants/drawer-ids.constant';
import { LocalAdministratorsComponent } from '../local-administrators/local-administrators.component';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { ErrorResponse } from '../../../core/models/error.models';
import { CommonService } from '../../../shared/services/common.service';
import { LoaderService } from '../../../core/services/loader.service';
import { ManageOrganizationService } from '../../manage-organization/manage-organization.service';
import { SearchSelectComponent } from '../../../components/search-select/search-select.component';
import {
  CountryItem,
  LookupItem,
  StateItem,
} from '../../../core/models/lookup.models';
import { LookupService } from '../../../core/services/lookup.service';
import { ManageOrganizationComponent } from '../../manage-organization/manage-organization.component';
import { AuditContextService } from '../../audit-logs/audit-context.service';
import { ApiResponse } from '../../../core/models/api-response.models';

@Component({
  selector: 'app-organization-form',
  standalone: true,
  imports: [
    NzFormControlComponent,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzSelectModule,
    NzFormModule,
    NzCheckboxModule,
    NzBreadCrumbModule,
    NzButtonModule,
    CommonModule,
    NzSpinModule,
    NzInputModule,
    SearchSelectComponent,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: './organization-form.component.html',
  styleUrl: './organization-form.component.scss',
})
export class OrganizationFormComponent implements OnInit, OnDestroy {
  @ViewChild('organizationFooterTpl') organizationFooterTpl!: TemplateRef<void>;
  @ViewChild('drawerTitleTpl') drawerTitleTpl!: TemplateRef<void>;
  private readonly searchSubject = new Subject<string>();

  readonly OrganizationMode = OrgMode;
  readonly OrgTitles = OrgTitles;
  organizationForm: FormGroup;
  organizationData: OrganizationModel = {} as OrganizationModel;
  fullAddress = '';
  orgParams: OrganizationDrawerParams = {
    organizationId: '',
    mode: OrgMode.CREATE,
    orgContact: '',
  };

  countries: CountryItem[] = [];
  states: StateItem[] = [];
  selectedParentOrganization: LookupItem | null = null;

  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  isOptionsLoading$ = this.loadingSubject.asObservable();

  private readonly destroy$ = new Subject<void>();

  loading$: Observable<boolean>;
  private readonly isLoading = new BehaviorSubject<boolean>(false);

  isLoading$ = this.isLoading.asObservable();

  constructor(
    private readonly fb: FormBuilder,
    private readonly drawerService: DrawerService,
    private readonly organizationService: OrganizationService,
    private readonly manageOrgService: ManageOrganizationService,
    private readonly commonService: CommonService,
    private readonly loaderService: LoaderService,
    private readonly lookupService: LookupService,
    private readonly auditContextService: AuditContextService
  ) {
    this.organizationForm = this.initializeForm();
    this.loading$ = this.loaderService.loading$;
    this.setupDrawerParams();
    this.setupCountrySubscription();
    this.setupZipCodeSearch();
  }

  ngOnInit(): void {
    this.loadCountries();
    if (this.isViewMode) {
      this.loadOrganizationData();
      this.organizationForm.disable();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isViewMode(): boolean {
    return this.orgParams.mode === this.OrganizationMode.VIEW;
  }

  get isEditMode(): boolean {
    return this.orgParams.mode === this.OrganizationMode.EDIT;
  }

  get isParentOrganization(): boolean {
    return (
      !this.organizationData.parentOrganizationId &&
      !this.organizationData.parentOrganizationName
    );
  }

  onEditOrganization(): void {
    this.orgParams.mode = this.OrganizationMode.EDIT;
    this.drawerService.updateDrawerTitle(
      DRAWER_IDS.ORGANIZATION,
      OrgTitles.EDIT
    );
    this.organizationForm.enable({ emitEvent: false });
  }

  onParentOrganizationSelected(option: LookupItem | null): void {
    this.selectedParentOrganization = option;
  }

  onSubmit(event: Event): void {
    event.preventDefault();
    if (this.organizationForm.invalid) return;

    const payload = this.createPayload();
    const operationType = this.isEditMode ? 'updated' : 'created';
    const _modifiedFields = this.buildOrganizationModifiedFields(payload);

    // Attach modifiedFields to payload for API (do not mutate statusId/parentOrganizationId)
    const apiPayload = { ...payload, _modifiedFields };

    this.loaderService.show();
    const operation$ = this.isEditMode
      ? this.organizationService.updateOrganization(
          apiPayload.id || 0,
          apiPayload
        )
      : this.organizationService.createOrganization(apiPayload);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: (response: ApiResponse<OrganizationModel>) => {
          if (operationType === 'updated' && response.result) {
            this.handleOrganizationDataLoad(response.result);
          }
          this.handleSuccess(operationType);
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  private buildOrganizationModifiedFields(
    payload: OrganizationModel
  ): unknown[] {
    const labelMap = this.getOrganizationAuditLabelMap();
    const { original, updated } = this.getOrganizationAuditContext(payload);
    const { enrichedOriginal, enrichedUpdated } = this.getEnrichedAuditModels(
      original,
      updated,
      labelMap
    );

    return this.auditContextService.buildFieldChanges(
      enrichedOriginal,
      enrichedUpdated,
      {
        sensitiveFields: ['address'],
        labelMap,
        excludeFields: this.getOrganizationAuditExcludeFields(),
      }
    );
  }

  private getOrganizationAuditLabelMap(): Record<string, string> {
    return {
      parentOrganizationId: 'Parent Organization',
      statusId: 'Status',
      organizationName: 'Organization Name',
      address: 'Address',
      city: 'City',
      state: 'State',
      zipCode: 'Zip Code',
      country: 'Country',
      productName: 'Product Name',
      isTrial: 'Trial',
      salesChannel: 'Sales Channel',
    };
  }

  private getOrganizationAuditExcludeFields(): string[] {
    const excludedFFields = [
      'id',
      'reviewedByAdminUserId',
      'subscriptionExpiry',
      'lastLogin',
      'logoUrl',
      'lmsSendDeadlineEmails',
      'lmsDefaultDeadlineDays',
      'subscriptionStatus',
    ];
    return this.isEditMode
      ? [...excludedFFields, 'parentOrganizationId', 'parentOrganizationName']
      : excludedFFields;
  }

  private getOrganizationAuditContext(payload: OrganizationModel): {
    original: Partial<OrganizationModel>;
    updated: Partial<OrganizationModel>;
  } {
    if (!this.isEditMode) {
      return {
        original: {},
        updated: payload,
      };
    }

    return {
      original: { ...this.organizationData },
      updated: { ...payload },
    };
  }

  private getOrganizationValueEnrichers(): Partial<
    Record<
      keyof OrganizationModel,
      (
        val: unknown,
        context: {
          original?: Partial<OrganizationModel>;
          updated?: Partial<OrganizationModel>;
        },
        source: 'original' | 'updated'
      ) => string | null
    >
  > {
    return {
      parentOrganizationId: (
        val: unknown,
        context: {
          original?: Partial<OrganizationModel>;
          updated?: Partial<OrganizationModel>;
        },
        source: 'original' | 'updated'
      ) => {
        const id = typeof val === 'number' ? val : Number(val);
        if (!id || id === 0) {
          return '';
        }

        if (source === 'original') {
          return (
            this.organizationData.parentOrganizationName ||
            (typeof context.original?.parentOrganizationName === 'string'
              ? context.original.parentOrganizationName
              : '')
          );
        }

        if (this.selectedParentOrganization?.id === id) {
          return this.selectedParentOrganization.value;
        }

        if (context.original?.parentOrganizationId === id) {
          return this.organizationData.parentOrganizationName || '';
        }

        return '';
      },
      statusId: (val: unknown) => {
        const id = typeof val === 'number' ? val : Number(val);
        if (id === 1) return 'Active';
        if (id === 2) return 'Inactive';
        return null;
      },
    };
  }

  private getEnrichedAuditModels(
    original: Partial<OrganizationModel>,
    updated: Partial<OrganizationModel>,
    labelMap: Record<string, string>
  ): {
    enrichedOriginal: Record<string, unknown>;
    enrichedUpdated: Record<string, unknown>;
  } {
    const valueEnrichers = this.getOrganizationValueEnrichers();

    const enrichFieldValue = (
      key: keyof OrganizationModel,
      value: unknown,
      context: {
        original?: Partial<OrganizationModel>;
        updated?: Partial<OrganizationModel>;
      },
      source: 'original' | 'updated'
    ): unknown => {
      if (valueEnrichers[key]) {
        return valueEnrichers[key]?.(value, context, source);
      }
      return value;
    };

    const enrichedOriginal: Record<string, unknown> = { ...original };
    const enrichedUpdated: Record<string, unknown> = { ...updated };
    const enrichKeys = Object.keys(labelMap) as (keyof OrganizationModel)[];

    for (const key of enrichKeys) {
      if (enrichedOriginal[key] !== undefined) {
        enrichedOriginal[key] = enrichFieldValue(
          key,
          enrichedOriginal[key],
          {
            original,
            updated,
          },
          'original'
        );
      }

      if (enrichedUpdated[key] !== undefined) {
        enrichedUpdated[key] = enrichFieldValue(
          key,
          enrichedUpdated[key],
          {
            original,
            updated,
          },
          'updated'
        );
      }
    }

    return {
      enrichedOriginal,
      enrichedUpdated,
    };
  }

  private handleSuccess(operationType: string): void {
    const message = `Organization ${operationType} successfully`;
    this.commonService.notify('success', message);
    this.closeOrganizationDrawer();
  }

  closeOrganizationDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.ORGANIZATION);
  }

  openOrganizationDrawer(): void {
    this.drawerService.openDrawer(
      ManageOrganizationComponent,
      this.drawerTitleTpl as unknown as TemplateRef<object>,
      1170,
      true,
      this.organizationFooterTpl as unknown as TemplateRef<object>,
      {
        organizationId: this.orgParams.organizationId,
        mode: OrgManagementMode.VIEW,
        organizationName: this.organizationData.organizationName,
        isParentOrganization: this.isParentOrganization,
      },
      DRAWER_IDS.MANAGE_ORG
    );
  }

  openAdministratorsDrawer(): void {
    this.drawerService.openDrawer(
      LocalAdministratorsComponent,
      this.drawerTitleTpl as unknown as TemplateRef<object>,
      520,
      true,
      this.organizationFooterTpl as unknown as TemplateRef<object>,
      {
        organizationId: this.orgParams.organizationId,
      },
      DRAWER_IDS.ADMINISTRATOR
    );
  }

  loadContactData(): void {
    const organizationId = Number(this.orgParams.organizationId);

    this.loaderService.show();
    this.organizationService
      .getContactsByOrgId(organizationId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: response => {
          this.openContactDrawer(response.result);
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  openContactDrawer(contactData?: UserResultModel): void {
    this.drawerService.openDrawer(
      OrgContactFormComponent,
      contactData ? OrgMode.VIEW_CONTACT : OrgMode.ADD_CONTACT,
      520,
      true,
      undefined,
      {
        organizationId: this.orgParams.organizationId,
        mode: contactData ? OrgMode.VIEW_CONTACT : OrgMode.ADD_CONTACT,
        orgContact: contactData ?? null,
      },
      DRAWER_IDS.CONTACT
    );
  }

  private initializeForm(): FormGroup {
    const zipCodePattern = /^\d{1,10}$/;
    const namePattern = /^[a-zA-Z0-9][a-zA-Z0-9\s_\-.,]*[a-zA-Z0-9]$/;
    const cityPattern = /^[a-zA-Z][a-zA-Z\s]*[a-zA-Z]$/;
    const addPattern = /^\S(.*\S)?$/;

    return this.fb.group({
      orgname: [
        '',
        [
          Validators.required,
          Validators.minLength(5),
          Validators.pattern(namePattern),
        ],
      ],
      orgadd: [
        '',
        [
          Validators.required,
          Validators.minLength(5),
          Validators.pattern(addPattern),
        ],
      ],
      orgcity: ['', [Validators.required, Validators.pattern(cityPattern)]],
      orgstate: [null, Validators.required],
      orgzipcode: [
        '',
        [
          Validators.required,
          Validators.pattern(zipCodePattern),
          Validators.minLength(1),
          Validators.maxLength(10),
        ],
      ],
      orgcountry: ['', [Validators.required]],
      orgparentorg: [''],
      active: [false],
      trial: [false],
      orgproductname: ['', Validators.pattern(namePattern)],
    });
  }

  private setupDrawerParams(): void {
    this.drawerService
      .getParams<OrganizationDrawerParams>(DRAWER_IDS.ORGANIZATION)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.orgParams = params;
        }
      });
  }

  private loadOrganizationData(): void {
    const organizationId = Number(this.orgParams.organizationId);
    if (!organizationId) return;
    this.loaderService.show();
    this.organizationService
      .getOrganization(organizationId)
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: this.handleOrganizationDataLoad.bind(this),
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  private setupZipCodeSearch(): void {
    this.searchSubject
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        tap(() => this.isLoading.next(true)),
        switchMap(value => {
          if (!value) {
            return of(null);
          }
          return this.lookupService.getLocationByPostalCode(value).pipe(
            map(response => response),
            catchError(error => {
              this.handleError(error);
              this.clearLocationFields();
              return of(null);
            }),
            finalize(() => this.isLoading.next(false))
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        if (result) {
          this.states = result.states;
          this.organizationForm.patchValue(
            {
              orgcity: result.city,
              orgstate: result.state,
              orgcountry: result.country,
            },
            { emitEvent: false }
          );
        } else {
          this.clearLocationFields();
        }
      });
  }

  private clearLocationFields(): void {
    this.states = [];
    this.organizationForm.patchValue(
      {
        orgcity: '',
        orgstate: '',
        orgcountry: '',
      },
      { emitEvent: false }
    );
  }

  onZipCodeChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchSubject.next(value);
  }

  private setupCountrySubscription(): void {
    this.organizationForm
      .get('orgcountry')
      ?.valueChanges.pipe(
        filter(value => !!value),
        takeUntil(this.destroy$)
      )
      .subscribe(country => {
        this.loadStatesForCountry(country, false);
      });
  }

  private loadStatesForCountry(country: string, preserveFields: boolean): void {
    if (!country) return;
    this.isLoading.next(true);
    this.lookupService
      .getStates(country)
      .pipe(
        catchError(error => {
          this.handleError(error);
          return of([]);
        }),
        finalize(() => this.isLoading.next(false)),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(states => {
        this.states = states;
        if (!preserveFields && !this.isViewMode) {
          this.organizationForm.patchValue(
            { orgstate: '', orgcity: '' },
            { emitEvent: false }
          );
        }
        if (states.length === 0) {
          this.organizationForm.patchValue(
            { orgstate: '' },
            { emitEvent: false }
          );
        } else if (preserveFields && this.organizationData.state) {
          this.organizationForm.patchValue(
            { orgstate: this.organizationData.state },
            { emitEvent: false }
          );
        }
      });
  }

  private loadCountries(): void {
    this.lookupService
      .getCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: countries => (this.countries = countries),
        error: error => this.handleError(error),
      });
  }

  private handleError(error: ErrorResponse): void {
    const errorMessages =
      error.errors?.map(err => err.detailedMessage || err.description) || [];
    this.commonService.notify('error', errorMessages, error.message);
  }

  private handleOrganizationDataLoad(response: OrganizationModel): void {
    this.organizationData = response;
    this.updateFullAddress(response);
    this.patchFormValues(response);
  }

  private updateFullAddress(org: OrganizationModel): void {
    this.fullAddress = `${org.address}, ${org.city}, ${org.state} , ${org.zipCode}, ${org.country}`;
  }

  numberOnly(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  private patchFormValues(org: OrganizationModel): void {
    this.organizationForm.patchValue(
      {
        id: org.id,
        orgname: org.organizationName,
        orgadd: org.address,
        orgcity: org.city,
        orgstate: org.state,
        orgzipcode: org.zipCode,
        orgcountry: org.country,
        orgproductname: org.productName,
        orgparentorg: org.parentOrganizationId,
        active: org.statusId !== 2,
        trial: org.isTrial,
        parentOrganizationName: org.parentOrganizationName,
      },
      { emitEvent: false }
    );

    this.selectedParentOrganization = org.parentOrganizationName
      ? {
          id: org.parentOrganizationId,
          value: org.parentOrganizationName,
        }
      : null;

    if (org.country) {
      this.loadStatesForCountry(org.country, true);
    }
  }

  private createPayload(): OrganizationModel {
    const formData = this.organizationForm.value;
    return {
      id: this.organizationData.id ?? 0,
      organizationName: formData.orgname,
      address: formData.orgadd,
      city: formData.orgcity,
      state: formData.orgstate,
      zipCode: formData.orgzipcode,
      country: formData.orgcountry,
      productName: formData.orgproductname,
      parentOrganizationId: formData.orgparentorg || 0,
      salesChannel: 'Test',
      statusId: formData.active ? 1 : 2,
      isTrial: formData.trial,
    };
  }

  closeManageOrgDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.MANAGE_ORG);
    this.manageOrgService.searchSubject.next({ searchTerm: '', tabIndex: 0 });
  }
}
